import { Cta } from '../styles';
import { Confirmed } from '@hqo/react-components-library/dist/icons';
import { StyledCounter } from 'pages/add-ons/components/styles';
import styled from 'styled-components';

/*
 * Cross-page imports are the settled pattern in this repo — `date-time-select-modal` is imported by four
 * page directories, `filter-modal` reaches into `date-time-select-page/styles`, and the shipped add-ons
 * page pulls its skeleton out of `room-layout`. The stepper is taken rather than rebuilt.
 *
 * ⛔ Its margins are overridden: the shipped one is laid out for a details modal (`margin: 32px 0 12px`),
 * and the design here puts the stepper inline on the row.
 */
/*
 * ⛔ THE STEPPER HAS NO POINTER, AND NOT BY OVERSIGHT HERE. RCL's `Counter` puts `cursor: pointer` on
 * the ICON -- which also carries `pointer-events: none`, so the cursor rule is on the one element
 * that can never be hovered. The blocks that actually take the click declare no cursor at all, so
 * the control reads as inert text on every consumer of that molecule. Overridden locally because
 * this ships now; the library itself wants the same fix, which is not this PR.
 *
 * The two halves are keyed SEPARATELY off the same disabled flags the stepper is already given, so a
 * held `+` (party locked, ranges in flight, at max) keeps the default arrow rather than promising a
 * press it will drop -- the inverse of the defect the row comments above already guard against. The
 * `-` is driven the same way at quantity zero.
 *
 * Hover is a background wash on the block, not a colour change on the icon: the icon's colour already
 * encodes enabled/disabled, and moving it on hover would make an enabled control momentarily read as
 * the other state.
 */
/*
 * ⛔ ONE HEIGHT FOR THE WHOLE CONTROL COLUMN, and it is shared rather than written twice because the
 * stepper and the fixed-quantity pill occupy the SAME column on sibling rows. A product with a range
 * renders the stepper and one without renders the pill, so two different numbers here make the rows
 * jump height as the eye travels down a menu that mixes them.
 */
const CONTROL_HEIGHT = '40px';

export const RowCounter = styled(StyledCounter)<{ isIncreaseDisabled?: boolean; isDecreaseDisabled?: boolean }>`
  margin: 0;
  align-self: center;
  flex-shrink: 0;

  /*
   * ⛔ RCL's \`Counter\` IS A STANDALONE CHECKOUT WIDGET, and none of that was being overridden here.
   * Its own \`Container\` declares \`width: 188px; height: 74px; padding: 23px 31px\` with a shadow and a
   * 32px radius, its glyphs 20px and its value \`500 24px/28px\` — sized for the details modal the
   * shipped add-ons page opens it in, where it is the only thing on screen. Dropped onto a 14px/12px
   * list row it was wider than the product name beside it.
   *
   * ⚠️ IT FELL THROUGH SILENTLY. Extending with \`styled()\` emits at EQUAL specificity and LATER, so
   * every property declared here wins — and every property NOT declared here keeps the library's
   * value. Overriding the margin and the two hit-blocks therefore read as "the stepper is styled for
   * this row" while the box, the glyph and the number were still the modal's.
   *
   * ⭐ THE HIT CIRCLES STAY 32px. The complaint is visual weight, and 32 is already under the 44px
   * touch target this step is used at on mobile at least as often as on desktop — shrinking the
   * target to answer a comment about glyph size would be a regression wearing a fix's clothes. The
   * glyph and the number come down; what the thumb lands on does not.
   */
  width: auto;
  height: ${CONTROL_HEIGHT};
  padding: 0;
  gap: 2px;
  border-radius: 0;
  box-shadow: none;

  .decrease-block,
  .increase-block {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    transition: background-color 120ms ease-in-out;
  }

  .decrease-icon,
  .increase-icon {
    font-size: 13px;
  }

  /*
   * ⚠️ A FLOOR AND A CENTRE, not just a smaller font. With the container no longer a fixed 188px the
   * row is content-sized, so a value crossing 9 → 10 widens the stepper and shunts the title beside
   * it. The floor holds two digits; beyond that it grows, which is the right trade for a quantity
   * nobody configures a ceiling above.
   */
  .count-block {
    font: 500 15px/20px ${({ theme }) => theme.fonts.join()};
    min-width: 20px;
    text-align: center;
  }

  .decrease-block {
    cursor: ${({ isDecreaseDisabled }) => (isDecreaseDisabled ? 'default' : 'pointer')};
  }

  .increase-block {
    cursor: ${({ isIncreaseDisabled }) => (isIncreaseDisabled ? 'default' : 'pointer')};
  }

  /*
   * ⚠️ Hover is gated on a hover-capable pointer so a touch device does not keep the wash latched after a tap -- this
   * step is reached on mobile at least as often as on desktop.
   */
  @media (hover: hover) {
    .decrease-block:hover {
      background-color: ${({ theme, isDecreaseDisabled }) =>
        isDecreaseDisabled ? 'transparent' : theme.colors.greys.adminGreyLight};
    }

    .increase-block:hover {
      background-color: ${({ theme, isIncreaseDisabled }) =>
        isIncreaseDisabled ? 'transparent' : theme.colors.greys.adminGreyLight};
    }
  }
`;

/*
 * Height matches the library `Counter` (188x74) so pill and stepper rows sit level; width does not.
 * ⛔ `nowrap` with no `max-width`: the LABEL cannot clip — it grows and `RowToggle` yields, so the
 *    PRODUCT NAME wraps sooner in de-DE, hu-HU, nl-NL, pt-PT (`RowTitle` has no `text-overflow`).
 * ⛔ The label names the ACTION: on a locked party holding a line, removal is all this control does.
 * ⛔ One border colour in both states: `adminGreyMediumAlt` is 2.82:1 on white, under the 3:1 for a
 *    control boundary. Background and label carry the selected difference instead.
 * ⚠️ NO BACKTICKS inside the template below — they terminate it, and the parse error points at the
 *    CSS rather than at the comment.
 */
export const RowPill = styled.button<{ isSelected?: boolean }>`
  flex-shrink: 0;
  align-self: center;
  min-width: 96px;
  /* Shared with the stepper — see CONTROL_HEIGHT; no backticks, per the note above. */
  min-height: ${CONTROL_HEIGHT};
  margin-right: 6px;
  padding: 10px 16px;
  border-radius: 999px;
  white-space: nowrap;
  cursor: pointer;
  font: inherit;
  border: 1px solid ${({ theme }) => theme.colors.greys.adminGreyDark};
  background: ${({ theme, isSelected }) =>
    isSelected ? theme.colors.greys.adminGreyLight : theme.colors.universal.white};
  color: ${({ theme }) => theme.colors.greys.adminBlack};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`;

export const Row = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.greys.adminGreyMediumAlt};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.universal.white};
`;

export const RowHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
`;

export const RowToggle = styled.button`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font-family: ${({ theme }) => theme.fonts.join()};

  /* A row with nothing to reveal is not a control; it still reads normally. */
  &:disabled {
    cursor: default;
    color: inherit;
  }
`;

export const RowTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  display: block;
  color: ${({ theme }) => theme.colors.fontColor};
`;

export const RowSubtitle = styled.span`
  font-size: 12px;
  display: block;
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
`;

export const RowDetails = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.greys.adminGreyMedium};
  padding: 12px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
`;

/*
 * RES-1359. The lead-time line, between the row header and any expanded description. A sibling of
 * `RowDetails` rather than a reuse: that one renders only while expanded, and this must be visible
 * on a collapsed row.
 */
export const RowNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  border-top: 1px solid ${({ theme }) => theme.colors.greys.adminGreyMedium};
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
`;

export const RowImage = styled.img`
  display: block;
  width: 100%;
  border-radius: 10px;
  margin-bottom: 10px;
`;

/*
 * One card per owning party.
 *
 * The SECTION owns the card and the rows inside it are stripped: `Row` was built standalone and is
 * already its own card, so nesting them unmodified renders rounded cards inside a rounded card. The
 * overrides at the foot of this block are scoped INSIDE the section, so `Row` keeps working wherever
 * it is used alone (the stories do that).
 *
 * No horizontal padding: `Row` already pads itself and the stepper is a fixed 188px, so at a 320px
 * viewport section padding would eat the row title rather than improve the layout.
 */
/*
 * A `div` with `role="group"`, deliberately NOT a `<section>`: a named `<section>` acquires
 * `role="region"`, putting every party card in the screen reader's landmark rotor. `group` is
 * defined as a set of objects not intended for the page summary, which is exactly this.
 */
export const PartyCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.greys.adminGreyMedium};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.universal.white};

  ${Row} {
    border: none;
    border-radius: 0;
    background: none;
  }

  ${Row} + ${Row} {
    border-top: 1px solid ${({ theme }) => theme.colors.greys.adminGreyMedium};
  }
`;

/*
 * The header band. Deliberately NOT `SectionHeader` from `pages/resource/styles.ts` -- that is a
 * 16px/600 heading with a bottom margin, made to float above a block.
 *
 * NEVER dimmed, even when the section is locked: it carries the party name, the only thing telling
 * the tenant WHICH party is unavailable, and at 50% opacity it computes to 2.86:1 against white.
 * WCAG 1.4.3 exempts inactive-control text; it does not exempt this.
 */
export const PartyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 12px;
  margin: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.greys.adminGreyMedium};
`;

export const PartyName = styled.span`
  flex: 1;
  min-width: 0;
  font-family: ${({ theme }) => theme.fonts.join()};
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.fontColor};
`;

/*
 * "Unavailable for this booking". Also never dimmed -- it explains the state, and it is the
 * accessible name the lock icon borrows.
 */
export const PartyLockedTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-family: ${({ theme }) => theme.fonts.join()};
  /* ⚠️ Explicit: the header renders as an <h2>, whose UA weight is bold and would be inherited. */
  font-weight: 400;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.greys.adminGreyDarkAlt};
`;

/*
 * Stands where the stepper would be on a locked row.
 *
 * This is the ENFORCEMENT, not a styling choice: `Counter`'s `isIncreaseDisabled` reaches only its
 * icon, so a "disabled" stepper is still clickable. Replacing the control renders no handler at all.
 *
 * The slot is NOT padded out to the stepper's 188px -- a locked section is a discrete state change,
 * so the one-time reflow is informative, and the freed width goes to the starved row title.
 */
export const LockedControl = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding-right: 6px;
`;

/*
 * The one-provider notice, above the list.
 *
 * NOT `components/alert/alert.tsx`: that is `role="alert"` (assertive, so it interrupts a screen
 * reader mid-sentence) and its props take `label` as a message id with no `values` pass-through, so
 * it cannot carry this string's `{party}` argument. `role="status"` is applied at the call site.
 */
export const OneProviderNotice = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  /*
   * PageContainer is a gapless flex column, so this owns its own separation from the summary card.
   * ⛔ VERTICAL ONLY — PageContainer owns the horizontal gutter. A 16px here stacked on its 16px and
   * pushed this band 32px off an edge the add-on rows sit 16px from.
   */
  margin: 12px 0 0;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.greys.adminGreyMedium};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.universal.white};
  font-family: ${({ theme }) => theme.fonts.join()};
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
`;

/*
 * The money summary, above the list.
 *
 * Its rows are `AddOnsLine` / `TotalLine` (summary-lines.tsx). VIS-2193 moved them off RCL's
 * `AddOnItem`, which takes a title and a price and nothing else, so an ordered line could not carry its
 * product image. (`ItemQuantityBlock` was never the alternative: it bakes in a 26px bottom margin,
 * truncates its title at 50% width, and renders a literal "0" badge.)
 */
export const SummaryCard = styled.div`
  display: flex;
  flex-direction: column;
  /* Vertical only: PageContainer owns the horizontal gutter. */
  margin: 12px 0 0;
  padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.greys.adminGreyMedium};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.universal.white};
`;

/*
 * VIS-2193. The order's rows, built here rather than on RCL's `AddOnItem`, which takes a title and a
 * price and nothing else, so a line could never carry its product image. Same type scale as the row
 * it replaces (16px, regular), the name taking the room and the amount holding its width.
 */
export const OrderedLineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  font: 400 16px/22px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};

  & + & {
    margin-top: 8px;
  }
`;

export const OrderedLineTitle = styled.span`
  flex: 1;
  min-width: 0;
`;

export const OrderedLineAmount = styled.span`
  flex-shrink: 0;
  text-align: right;
`;

/* The total at 700 on both sides, under a rule — as the design renders it. */
export const OrderedTotalRow = styled(OrderedLineRow)`
  margin-top: 7px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.greys.adminGreyMedium};
  font-weight: 700;

  & + & {
    margin-top: 7px;
  }
`;

export const BilledAfterNote = styled.div`
  font-family: ${({ theme }) => theme.fonts.join()};
  font-size: 12px;
  line-height: 1.4;
  margin-top: 6px;
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
`;

/* RES-1357. Why the total above leaves something out. Its own alias, as the notes beside it are. */
export const QuoteExcludedNote = styled(BilledAfterNote)``;

/*
 * RES-1336 — the receipt line under the total, once a charge has settled. Deliberately the same
 * quiet treatment as `BilledAfterNote` directly above: both are footnotes to the figure, and a
 * "paid" line styled louder than the total would read as the headline of a screen whose headline is
 * already "Add-ons ordered".
 */
export const PaidNote = styled.div`
  font-family: ${({ theme }) => theme.fonts.join()};
  font-size: 12px;
  line-height: 1.4;
  margin-top: 6px;
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
`;

/* The questions screen's heading row. The back control is the modal header's — see `step-header.context`. */
export const QuestionsHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
`;

/*
 * The provider's identity above an order. Rendered ONLY when a name resolved — an initials bubble
 * built from an absent name would show an empty circle, which reads as a broken avatar rather than
 * as missing data. The intro string below carries a party-less fallback for that case.
 *
 * RES-1367: no bottom margin. The rows below bring their own lead-in, so this was a second gap
 * stacked on the first and the provider floated away from the order it belongs to.
 */
export const PartyIdentity = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`;

/*
 * Pushes the order's status pill to the trailing edge of the party row. The push is on the pill, not
 * on the row: `PartyIdentity` is shared with a screen that has no pill, and `space-between` there
 * would move the disc and the name apart everywhere. A wrapper rather than
 * `styled(StatusPillContainer)` — that pill belongs to the redesigned tile's sealed folder.
 */
export const PartyStatus = styled.div`
  margin-left: auto;
`;

/*
 * The questions screen's party disc, and after RES-1367 the ONLY one: the ordered-add-ons list took
 * `@hqo/entity-icon`'s `EntityIcon` instead, which brings its own circle, its own fallback and its
 * own broken-image handling. This stays because that screen's header is a different row at a
 * different size, not because two discs are wanted.
 *
 * ⚠️ The circle is sized from the TEXT, not the other way round: 15px initials in a 32px disc left
 * almost no ring around them and read as cramped beside the party name. 40px gives the glyphs room
 * without the bubble dominating the row.
 */
export const PartyInitials = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.greys.adminGreyLight};
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.greys.adminBlack};
  display: flex;
  flex: 0 0 auto;
  font: 500 15px/1 ${({ theme }) => theme.fonts.join()};
  height: 40px;
  justify-content: center;
  width: 40px;
`;

/*
 * RES-1316 — why there is no total on an unpriced order. Same quiet treatment as the notes beside
 * it: it stands in for a figure, so dressing it louder than the lines would make the absence read
 * as the headline.
 */
export const AwaitingPriceNote = styled(PaidNote)``;

/*
 * RES-1316 — money already returned to the tenant. Beside the total rather than taken off it: the
 * charge was made and the refund is a second movement, and a receipt that silently nets them shows a
 * figure that appears on no statement either side.
 */
export const RefundNote = styled(PaidNote)``;

export const PartyIntro = styled.p`
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
  font: 400 14px/20px ${({ theme }) => theme.fonts.join()};
  margin: 0 0 16px;
`;

/*
 * The unowned-item warning. Same band as `OneProviderNotice` and the same `role="status"` for the same
 * reason — a passive explanation must not interrupt a screen reader mid-sentence — but its own component
 * so the two can diverge without one inheriting the other's spacing by accident.
 */
export const UnresolvedNotice = styled(OneProviderNotice)``;

/*
 * RES-1326 — the confirmation screen.
 */
export const OrderedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  /* Vertical only: PageContainer owns the horizontal gutter. */
  padding: 16px 0 0;
`;

export const OrderedIcon = styled(Confirmed)`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
`;

export const OrderedBookingCard = styled.div`
  display: grid;
  gap: 4px;
  /* Vertical only: PageContainer owns the horizontal gutter. */
  margin: 16px 0;
  padding: 16px;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.greys.adminGreyMedium};
  border-radius: 8px;
`;

export const OrderedBookingName = styled.span`
  font: 500 16px/22px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
`;

export const OrderedBookingWhen = styled.span`
  font: 400 14px/20px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
`;

/*
 * Styled here rather than imported from `booking-tile/redesigned/styles`: the LOGIC is worth reusing
 * (`getStatusPillLabelId` carries this app's tenant-facing status vocabulary, and the label is already
 * translated in all 19 catalogues) but that folder is a flagged redesign whose styles will move or go
 * when the flag does, and this screen is not behind that flag. Same variant-to-colour mapping.
 */
export const OrderedStatusPill = styled.span<{ variant: 'positive' | 'pending' | 'negative' }>`
  justify-self: start;
  font: 500 13px/1 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme, variant }) => {
    if (variant === 'positive') return theme.colors.palette.systemGreen;
    if (variant === 'pending') return theme.colors.palette.systemOrange;
    return theme.colors.palette.systemPink;
  }};
`;

export const SecondaryCta = styled(Cta)`
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.fontColor};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.greys.adminGreyMedium};
`;
